import {
  ComponentProps,
  ComponentType,
  useCallback,
  useContext,
  useMemo,
  type ReactNode
} from 'react';
import Tooltip from 'antd/lib/tooltip';

import ASwitch from 'antd/lib/switch';

import { Popconfirm } from 'antd';
import { Hint } from 'components/Hint';
import { useField } from 'react-final-form';
import clsx from 'clsx';
import { useLingui } from '@lingui/react/macro';
import { useAttributeLabel } from '../useAttributeLabel';

import { FormControlBaseProps } from '../FormControl';
import { FormContext } from '../FormContext';
import styles from './Switch.module.scss';

interface SwitchBaseProps extends ComponentProps<typeof ASwitch> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string;
  confirm?: string;
  withConfirmation?: boolean;
  readOnly?: boolean;
}

function SwitchBase({
  label,
  hint,
  className,
  error,
  readOnly,
  checked,
  withConfirmation,
  onChange,
  ...props
}: SwitchBaseProps) {
  return (
    <label className={clsx(styles.switch, className)}>
      {label && (
        <>
          <span className={styles.label}>
            {label}
            {hint && <Hint hint={hint} />}
          </span>
          &nbsp;
        </>
      )}
      <Tooltip visible={!!error} color="red" title={error}>
        <span className="tooltip-anchor" />
      </Tooltip>
      <ASwitch
        {...props}
        disabled={props.disabled || readOnly}
        checked={checked}
        onChange={withConfirmation ? undefined : onChange}
      />
    </label>
  );
}

type SwitchControlProps = FormControlBaseProps &
  Omit<
    SwitchBaseProps,
    'hint' | 'error' | 'label' | 'readOnly' | 'checked' | 'onChange'
  >;

function SwitchControl({
  label: labelProp,
  className,
  hint: hintProp,
  name,
  withConfirmation,
  ...props
}: SwitchControlProps) {
  const {
    input: { checked, onChange },
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    meta: { error }
  } = useField(name, { ...props, type: 'checkbox' });

  const { readOnly } = useContext(FormContext);

  const attributeLabel = useAttributeLabel(name);

  const label = useMemo(
    () => labelProp ?? attributeLabel.label,
    [attributeLabel, labelProp]
  );
  const hint = useMemo(
    () => hintProp ?? attributeLabel.hint,
    [attributeLabel, hintProp]
  );
  return (
    <SwitchBase
      {...props}
      hint={hint}
      error={error as string}
      label={label}
      readOnly={readOnly}
      checked={checked}
      onChange={onChange}
    />
  );
}

function withConfirm(Component: ComponentType<SwitchControlProps>) {
  function ComposedSwitch({
    confirm: confirmProp,
    name,
    ...props
  }: Omit<SwitchControlProps, 'withConfirmation'>) {
    const attributeLabel = useAttributeLabel(name);
    const confirm = useMemo(
      () => confirmProp || attributeLabel.confirm,
      [attributeLabel.confirm, confirmProp]
    );

    const {
      input: { checked, value, onChange }
    } = useField<unknown>(name, { subscription: { value: true } });
    const shouldConfirm = useMemo(
      () => !(value ?? checked) && Boolean(confirm),
      [value, confirm, checked]
    );
    const { t } = useLingui();
    const handleConfirm = useCallback(() => onChange?.(true), [onChange]);
    const handleDecline = useCallback(() => onChange?.(false), [onChange]);

    if (!shouldConfirm) return <Component {...props} name={name} />;

    return (
      <Popconfirm
        title={confirm}
        okText={t`Да`}
        cancelText={t`Нет`}
        onConfirm={handleConfirm}
        onCancel={handleDecline}
      >
        <Component name={name} {...props} withConfirmation />
      </Popconfirm>
    );
  }
  ComposedSwitch.displayName = 'SwitchWithConfirm';
  return ComposedSwitch;
}

export const Switch = Object.assign(withConfirm(SwitchControl), {
  Base: SwitchBase
});
